import { Dispatch, FormEvent, ForwardedRef, SetStateAction, useCallback, useEffect, useRef, useState } from "react";

import { FileType, FileUploadHandle } from "../../../../../../models";
import { uploadOrganisationFile } from "../../../../../../service/organisation";
import { ServiceError, Status } from "../../../../../../service/Shared";
import { useAuth } from "../../../../../../useAuth";
import { useIsLoadingWrapper } from "../../../../../../utils";
import { Toast } from "../../../../../../widget";
import { OrganisationFormChangedAction, OrganisationFormChangedEnum } from "../../models";

interface UseBannerFormReturnData {
  bannerUploadRef: ForwardedRef<FileUploadHandle>;
  displayedBanner: string | null;
  errors: ServiceError[] | undefined;
  isHandleSubmitLoading: boolean;
  currentUserType: string;
  setBanner: Dispatch<SetStateAction<File | null>>;
  handleSubmit: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  handleCancel: () => void;
  hasPermission: (permission: string) => boolean;
}

export const useBannerForm = (
  objectUuid: string,
  objectType: string,
  organisationDefaultBanner: string | null,
  dispatch: Dispatch<OrganisationFormChangedAction>
): UseBannerFormReturnData => {
  const { hasPermission, currentUserType } = useAuth();

  const [submittedValues, setSubmittedValues] = useState<File | null>(null);

  const bannerUploadRef = useRef<FileUploadHandle>(null);
  const [banner, setBanner] = useState<File | null>(null);
  const [displayedBanner, setDisplayedBanner] = useState<string | null>(organisationDefaultBanner);
  const [isHandleSubmitLoading, setIsHandleSubmitLoading] = useState(false);
  const [errors, setErrors] = useState<ServiceError[] | undefined>();

  const resetForm = (): void => {
    if (bannerUploadRef.current) {
      bannerUploadRef.current.clearInput();
      setBanner(null);
      setSubmittedValues(null);
      setErrors([]);
    }
  };

  const getCurrentFormData = useCallback((): File | null => {
    return banner;
  }, [banner]);

  useEffect(() => {
    dispatch({
      type: OrganisationFormChangedEnum.SET_BANNER_FORM_CHANGED,
      value: submittedValues !== getCurrentFormData(),
    });
  }, [banner, submittedValues]);

  const handleSubmit = useIsLoadingWrapper(async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setErrors([]);

    if (objectUuid && banner) {
      const res = await uploadOrganisationFile({
        objectUuid,
        objectType,
        type: FileType.Banner,
        file: banner,
      });

      if (res.status === Status.Success && res.data && bannerUploadRef.current) {
        Toast.success({ message: "Organisation banner changed successfully" });
        setSubmittedValues(banner);
        bannerUploadRef.current.clearInput();
        setDisplayedBanner(res.data?.file.url);
      }

      if (res.status === Status.Error) {
        setErrors(res.errors);
      }
    }
  }, setIsHandleSubmitLoading);

  const handleCancel = (): void => {
    resetForm();
  };

  return {
    bannerUploadRef,
    displayedBanner,
    errors,
    isHandleSubmitLoading,
    currentUserType,
    setBanner,
    handleSubmit,
    handleCancel,
    hasPermission,
  };
};
